import { QUESTION_TYPES, questionTypeHint, questionTypeLabel, type QuestionType } from '@survey/core';
import { Check, ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { useI18n } from '../../i18n';
import { Modal } from '../ui';
import { TYPE_ICONS } from './model';

/** Grid of question types; `current` is highlighted. */
export function TypeGrid({ current, onPick }: { current?: QuestionType; onPick: (type: QuestionType) => void }) {
  const { locale } = useI18n();
  return (
    <div className="type-grid">
      {QUESTION_TYPES.map((qt) => {
        const Icon = TYPE_ICONS[qt.type];
        const active = qt.type === current;
        return (
          <button key={qt.type} type="button" className={`type-btn ${active ? 'active' : ''}`} aria-pressed={current ? active : undefined} onClick={() => onPick(qt.type)} autoFocus={active}>
            <span className="tile">
              <Icon />
            </span>
            {active && <Check className="type-check" aria-hidden />}
            <strong>{questionTypeLabel(qt.type, locale)}</strong>
            <span className="type-hint">{questionTypeHint(qt.type, locale)}</span>
          </button>
        );
      })}
    </div>
  );
}

/** Field-style button showing the current type; opens a modal to pick another. */
export function TypePicker({ id, value, onChange, className = '', label }: { id?: string; value: QuestionType; onChange: (type: QuestionType) => void; className?: string; label?: string }) {
  const { t, locale } = useI18n();
  const [open, setOpen] = useState(false);
  const Icon = TYPE_ICONS[value];
  return (
    <>
      <button id={id} type="button" className={`select type-picker ${className}`} aria-haspopup="dialog" aria-label={label} title={label} onClick={(e) => { e.stopPropagation(); setOpen(true); }}>
        <Icon className="type-picker-icon" aria-hidden />
        <span className="grow">{questionTypeLabel(value, locale)}</span>
        <ChevronDown className="type-picker-chev" aria-hidden />
      </button>
      {open && (
        <Modal title={t('qe.change_type')} onClose={() => setOpen(false)} wide>
          <TypeGrid
            current={value}
            onPick={(type) => {
              if (type !== value) onChange(type);
              setOpen(false);
            }}
          />
          <p className="hint" style={{ marginTop: 12 }}>
            {t('qe.change_type_hint')}
          </p>
        </Modal>
      )}
    </>
  );
}
